// @flow

import React, {PureComponent} from 'react';
import Dropzone from 'react-dropzone';
import classNames from 'classnames';
import {FormattedMessage, injectIntl} from 'react-intl';
import {ButtonIcon} from '@homeaway/react-buttons';
import {CLOSE__24} from '@homeaway/svg-defs';
import {times} from 'lodash';
import Compress from 'compress.js';
import {triggerGiftCardPhotoCompressFailureEvent} from '../../../utils/edapUtils';

import {MAX_IMAGE_SIZE, ERROR_UPLOAD_IMAGE, ERROR_FILE_TYPE_NOT_SUPPORTED, ERROR_MAX_IMAGE_SIZE, DROP_FILE_TEXT, REMOVE_IMAGE_TEXT} from '../../../utils/constants';
import {ADD_PHOTO_LABEL} from '../GiftCardInformation/GiftCardInformation';
import {getFormattedMessage} from '../../../utils/validations';
import logger from '../../../utils/clientLogger';

import './GiftCardPhotoUploader.less';

const DROP_FILE_LABEL = (
    <FormattedMessage
        id="giftCardDropFIleText"
        defaultMessage={DROP_FILE_TEXT}
    />
);

class GiftCardPhotoUploader extends PureComponent {
    state = {
        file: {}
    };

    componentWillUnmount() {
        const preview = this.state.file && this.state.file.preview;
        /* istanbul ignore else */
        if (preview) {
            URL.revokeObjectURL(this.state.file.preview);
        }
    }

    clearUploadedImage = () => {
        this.setState({file: {}});
        this.props.input.onChange('');
    }

    compressImageCompressor = async (acceptedFile) => {
        let compress = new Compress();
        let compressedImage = await compress.compress(acceptedFile, {
            size: 2, // the max size in MB, defaults to 2MB
            quality: 0.75, // the quality of the image, max is 1,
            maxWidth: 1920, // the max width of the output image, defaults to 1920px
            maxHeight: 1920, // the max height of the output image, defaults to 1920px
            resize: true, // defaults to true, set false if you do not want to resize the image width and height
        });
        return compressedImage && compressedImage[0];
    }

    uploadImage = (acceptedFile) => {
        try {
            const formData = new FormData();
            /* istanbul ignore else */
            if (acceptedFile && acceptedFile[0]) {
                let binaryString;
                let bytes;
                const image = document.createElement('image');
                image.src = URL.createObjectURL(acceptedFile && acceptedFile[0]);
                /* istanbul ignore next */
                this.compressImageCompressor(acceptedFile).then((imageFile) => {
                    this.setState({file: {...acceptedFile, preview: image.src}});
                    binaryString = atob(imageFile.data);
                    let lengthOfString = binaryString.length;
                    bytes = new Uint8Array(lengthOfString);

                    times(lengthOfString, (index) => {
                        bytes[index] = binaryString.charCodeAt(index);
                    });

                    formData.append('file', new Blob([bytes]), acceptedFile[0].name);
                    this.props.input.onChange({data: Object.values(bytes),
                        type: acceptedFile[0].type
                    });
                });
            }
        } catch (err) {
            logger.captureException(`[giftcard.photo.compress.failure] ${acceptedFile[0].name} : ${err}`);
            triggerGiftCardPhotoCompressFailureEvent(`File compression failed ${acceptedFile[0].name}`); // TODO check what data needs to be sent
        }
    };

    showImageErrors = (linguaKey, errorMessage) => (
        <span
            className="help-block"
            role="alert"
            aria-live="polite"
            aria-label={this.props.intl.formatMessage({
                id: linguaKey,
                defaultMessage: errorMessage
            })}
        >
            {getFormattedMessage(linguaKey, errorMessage)}
        </span>
    );

    render() {
        const {meta, giftCardErrors, giftCardCount, input, intl} = this.props;
        const {file} = this.state;
        const linguaKey = 'giftCardFormPhotoErrorMessage';
        const errorMessage = ERROR_UPLOAD_IMAGE;

        const isImageNotUploaded = meta && meta.submitFailed && meta.invalid && file && !file.preview && giftCardErrors.filter((image) =>
            (image === `giftCardImageUploader${giftCardCount}`));

        return (
            <Dropzone onDrop={this.uploadImage}
                accept="image/gif, image/jpeg, image/jpg, image/png"
                multiple={false}
                maxSize={MAX_IMAGE_SIZE}
            >
                {({getRootProps, getInputProps, isDragReject, rejectedFiles}) => {
                    /* istanbul ignore next */
                    const isFileTooLarge = rejectedFiles.length > 0 && rejectedFiles[0].size > MAX_IMAGE_SIZE;
                    /* istanbul ignore next */
                    return (
                        <div
                            className={classNames('GiftCardPhotoUploader_wrapper', {
                                'has-error': isImageNotUploaded || isFileTooLarge || isDragReject
                            })}
                        >
                            <div className="GiftCardPhotoUploader">
                                {file && !file.preview && <div {...getRootProps({className: 'dropzone'})}>
                                    <input {...getInputProps()} name={input.name} id="myFile"/>

                                    <span className="GiftCardPhotoUploader_AddFile">{ADD_PHOTO_LABEL}{' '}</span><span>{DROP_FILE_LABEL}</span>
                                </div>}
                            </div>
                            {file && file.preview &&
                            <div key={`GiftCardPhotoUploader__ThumbsContainer${giftCardCount}`} className="GiftCardPhotoUploader__ThumbsContainer">
                                <div className="GiftCardPhotoUploader__Thumb">
                                    <div className="GiftCardPhotoUploader__ThumbInner">
                                        <img alt="preview of Gift card uploaded"
                                            src={file.preview}
                                            className="GiftCardPhotoUploader__Image"
                                        />
                                    </div>
                                </div>
                                <ul className="GiftCardPhotoUploader__FileDetails">
                                    <li className="File_name">{file[0] && file[0].path}</li>
                                    <li className="File_size" key={file[0] && file[0].name}>
                                        {Number.parseFloat(file[0] && file[0].size / 1024).toFixed(1)} {'KB'}
                                    </li>
                                </ul>
                                <div className="display--flex">
                                    <ButtonIcon onClick={this.clearUploadedImage} className="GiftCardPhotoUploader__Clear_Upload" label={intl.formatMessage({
                                        id: 'giftCardRemoveImageLabel',
                                        defaultMessage: REMOVE_IMAGE_TEXT
                                    })} svgMarkup={CLOSE__24} circle size="xs"
                                    />
                                </div>
                            </div>
                            }
                            {isImageNotUploaded && this.showImageErrors(linguaKey, errorMessage)}
                            {/* istanbul ignore next */
                                isFileTooLarge && this.showImageErrors('giftCardImageSizeError', ERROR_MAX_IMAGE_SIZE)}
                            {/* istanbul ignore next */
                                isDragReject && this.showImageErrors('giftCardPhotoTypeError', ERROR_FILE_TYPE_NOT_SUPPORTED)}
                        </div>
                    );
                }
                }
            </Dropzone>
        );
    }
}

export default injectIntl(GiftCardPhotoUploader);
